import type { ComponentType } from "react";

import {
  AgentFileIcon,
  AudienceIcon,
  BulbSuggestionIcon,
  CanvasIcon,
  ChatIcon,
  EmailIcon,
  FileIcon,
  FrameStackIcon,
  GlobeIcon,
  type IconBoxProps,
  ImageIcon,
  LinkIcon,
  SparkleIcon,
  SyncIcon,
  SmsIcon,
  PushIcon,
} from "@hightouch/htui";
import { format } from "date-fns";

import { UNTITLED_CAMPAIGN_NAME } from "@hightouch/lib/llm/lifecycle-studio/campaigns/types";

import type { AmpProjectLinkFragment } from "src/graphql";
import { describeActionPlanLink } from "src/pages/projects/action-plans";
import { libraryFilePath } from "src/pages/projects/constants";
import { adDisplayName } from "src/pages/ad-studio/components/ad-display-name";
import { getOpportunityDetailPath } from "src/pages/ad-studio/insights/opportunities/use-opportunity-card-actions";
import { LifecycleStudioIcon } from "src/ui/icons/lifecycle-studio";

export const LINK_KIND_LABELS = {
  campaign: "Campaign",
  chatSession: "Chat",
  ad: "Ad",
  webPage: "Web page",
  document: "Document",
  slides: "Slides",
  file: "File",
  canvas: "Canvas",
  brief: "Brief",
  audience: "Audience",
  sync: "Sync",
  email: "Email",
  sms: "SMS",
  push: "Push notification",
  opportunity: "Opportunity",
  action: "Agent action",
  actionPlan: "Action plan",
  external: "Link",
  unknown: "Attachment",
};
export type LinkKind = keyof typeof LINK_KIND_LABELS;

export const KIND_ICONS: Record<
  LinkKind,
  { icon: ComponentType; color: IconBoxProps["color"] }
> = {
  campaign: { icon: LifecycleStudioIcon, color: "teal" },
  chatSession: { icon: ChatIcon, color: "indigo" },
  ad: { icon: ImageIcon, color: "pink" },
  webPage: { icon: GlobeIcon, color: "blue" },
  document: { icon: FileIcon, color: "gray" },
  slides: { icon: FrameStackIcon, color: "indigo" },
  file: { icon: FileIcon, color: "gray" },
  canvas: { icon: CanvasIcon, color: "indigo" },
  brief: { icon: AgentFileIcon, color: "orange" },
  audience: { icon: AudienceIcon, color: "blue" },
  sync: { icon: SyncIcon, color: "green" },
  email: { icon: EmailIcon, color: "teal" },
  sms: { icon: SmsIcon, color: "teal" },
  push: { icon: PushIcon, color: "teal" },
  opportunity: { icon: BulbSuggestionIcon, color: "yellow" },
  action: { icon: SparkleIcon, color: "green" },
  actionPlan: { icon: SparkleIcon, color: "green" },
  external: { icon: GlobeIcon, color: "gray" },
  unknown: { icon: LinkIcon, color: "gray" },
};

export type DescribedLink = {
  kind: LinkKind;
  title: string;
  subtitle: string | null;
  /** An in-app path relative to the workspace, or a full URL (see `isExternalHref`). */
  href: string | null;
};

const CAMPAIGN_BRIEF_ARTIFACT_TYPE = "campaign_brief";

// A Library file's type, for the files an ad or email link doesn't already describe.
const FILE_LINK_KINDS = new Map<string, LinkKind>([
  ["html", "webPage"],
  ["doc", "document"],
  ["slides", "slides"],
  ["upload", "file"],
  ["canvas", "canvas"],
  ["sms", "sms"],
  ["push", "push"],
]);

/** A Lifecycle Studio campaign, which opens on its current tab. */
function campaignPath(campaignId: string): string {
  return `/lifecycle-studio/projects/${campaignId}`;
}

/** The campaign's Planning tab, where its brief reads. */
function campaignBriefPath(campaignId: string): string {
  return `${campaignPath(campaignId)}/planning`;
}

export function isCampaignBrief(
  artifact: { artifact_type: string } | null,
): boolean {
  return artifact?.artifact_type === CAMPAIGN_BRIEF_ARTIFACT_TYPE;
}

export function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/** What an attachment points at, and where clicking it goes. */
export function describeLink(link: AmpProjectLinkFragment): DescribedLink {
  const inApp = (
    kind: LinkKind,
    title: string,
    href: string | null,
    subtitle: string | null = null,
  ): DescribedLink => ({ kind, title, subtitle, href });

  if (link.llm_chat_session) {
    return inApp(
      "chatSession",
      link.llm_chat_session.name || "Untitled chat",
      `/agents/chats/${link.llm_chat_session.id}`,
      `Started ${format(new Date(link.llm_chat_session.created_at), "MMM d")}`,
    );
  }
  if (link.marketing_campaign) {
    return inApp(
      "campaign",
      link.marketing_campaign.name || UNTITLED_CAMPAIGN_NAME,
      campaignPath(link.marketing_campaign.id),
    );
  }
  if (link.campaign_artifact) {
    const artifact = link.campaign_artifact;
    const channel: unknown = artifact.metadata?.channel;
    if (channel === "sms" || channel === "push") {
      return inApp(
        channel,
        artifact.title,
        `/lifecycle-studio/messages/copy/${artifact.id}`,
      );
    }
    const isBrief = isCampaignBrief(artifact);
    const campaignId = artifact.campaign_id;
    return inApp(
      isBrief ? "brief" : "unknown",
      artifact.title || (isBrief ? "Campaign brief" : LINK_KIND_LABELS.unknown),
      campaignId
        ? isBrief
          ? campaignBriefPath(campaignId)
          : campaignPath(campaignId)
        : null,
    );
  }
  if (link.ad_studio_project_brief_version) {
    const brief = link.ad_studio_project_brief_version;
    return inApp(
      "brief",
      brief.ad_studio_project
        ? `${brief.ad_studio_project.name} brief`
        : "Brief",
      brief.ad_studio_project
        ? `/ad-studio/projects/${brief.ad_studio_project.id}`
        : null,
      `Version from ${format(new Date(brief.created_at), "MMM d")}`,
    );
  }
  if (link.audience) {
    return inApp(
      "audience",
      link.audience.name,
      `/audiences/${link.audience.id}`,
    );
  }
  if (link.sync) {
    const { segment } = link.sync;
    // The destination shows among the row's facts.
    return inApp(
      "sync",
      segment ? segment.name : `Sync ${link.sync.id}`,
      `/syncs/${link.sync.id}`,
    );
  }
  const file = link.amp_file;
  if (file) {
    // Every Library file opens in the Library, ads and emails included.
    const href = libraryFilePath({ id: file.id, canvasId: file.canvas_id });
    const ad = file.ad_brief_asset;
    if (ad) {
      return inApp(
        "ad",
        adDisplayName({
          assetName: ad.asset_name,
          summary: ad.ad_brief?.summary,
        }),
        href,
      );
    }
    const email = file.content_email;
    if (email) {
      return inApp("email", email.name, href, email.subject || null);
    }
    const kind = FILE_LINK_KINDS.get(file.type) ?? "unknown";
    return inApp(kind, file.name || LINK_KIND_LABELS[kind], href);
  }
  if (link.amp_opportunity) {
    return inApp(
      "opportunity",
      link.amp_opportunity.headline,
      getOpportunityDetailPath(link.amp_opportunity.id),
      link.amp_opportunity.summary || null,
    );
  }
  if (link.amp_action) {
    const chatId = link.amp_action.llm_chat_session_id;
    return inApp(
      "action",
      link.amp_action.title,
      chatId ? `/agents/chats/${chatId}` : null,
    );
  }
  if (link.amp_action_plan) {
    return describeActionPlanLink(link.amp_action_plan);
  }
  const external = parseExternal(link.external);
  if (external) {
    return {
      kind: "external",
      title: external.label,
      subtitle: external.host,
      href: external.href,
    };
  }
  return inApp("unknown", LINK_KIND_LABELS.unknown, null);
}

function parseExternal(
  value: unknown,
): { label: string; href: string | null; host: string | null } | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const label = typeof record.label === "string" ? record.label : null;
  if (!label) return null;
  const href =
    typeof record.href === "string" && isExternalHref(record.href)
      ? record.href
      : null;
  return { label, href, host: href ? hostOf(href) : null };
}

function hostOf(href: string): string | null {
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

// The order work moves through a task: campaign, brief, audience, message, sync, launch.
const ATTACHMENT_KIND_ORDER: Record<LinkKind, number> = {
  campaign: 0,
  ad: 1,
  brief: 2,
  document: 3,
  slides: 4,
  audience: 5,
  email: 6,
  sms: 7,
  push: 8,
  webPage: 9,
  canvas: 10,
  sync: 11,
  actionPlan: 12,
  action: 13,
  opportunity: 14,
  chatSession: 15,
  file: 16,
  external: 17,
  unknown: 18,
};

/** Sorts attachments by the stage of the work they belong to. */
export function compareLinkKinds(a: LinkKind, b: LinkKind): number {
  return ATTACHMENT_KIND_ORDER[a] - ATTACHMENT_KIND_ORDER[b];
}

/** A task's attachments, described and in the order its card shows them. */
export function describeTaskAttachments(task: {
  attachments: AmpProjectLinkFragment[];
}): Array<DescribedLink & { id: string }> {
  return task.attachments
    .map((link) => ({ id: link.id, ...describeLink(link) }))
    .sort((a, b) => compareLinkKinds(a.kind, b.kind));
}
